import React from 'react';
import styled from 'styled-components';
import RCore from 'RCore';
import _ from 'lodash';
import { SimpleCard } from 'SportIcons';
import MatchScore from './MatchScore';
import PropTypes from 'prop-types';

import { PlayerLogo3DJerseyBack as PlayerLogo } from '../../../components/sm/views/playerLogo/SMPlayerLogo';

let dict = RCore.i18Register(require('../../../i18/i18.IncidentWidget.js'), {});
let i18 = RCore.getTranslation.bind({ dict });

const MainContent = styled.div`
    display: flex;
    justify-content: center;
    padding: 8px 10px 20px;
`;

const ContentWrapper = styled.div`
    text-align: center;
    background-color: ${props => props.theme.content.backgroundColor};
`;

const Text = styled.span`
    font-size: ${props => props.size}px;
    line-height: 1.1em;
    display: inline-block;
    white-space: nowrap;
    font-weight: ${props => (props.bold ? 'bold' : '400')};
`;
const Space = styled.div`
    height: ${props => props.size}px;
`;

const CardIconWrapper = styled.div`
    transform: rotate(15deg) translateY(-4px);
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.5);
    border-radius: 3px;
    overflow: hidden;
    display: inline-block;
    margin: 10px 0;
`;

const SubstitutionWrapper = styled.div`
    .flex {
        display: flex;
        width: 100%;
        flex-wrap: nowrap;
        flex-direction: row;
        text-align: left;

        > div {
            flex: 0 1 50%;

            .t-shirt-subst-in {
                margin-left: -80px;
                margin-top: 90px;
            }
        }
    }
`;

const SubstitutionType = styled.div`
    font-size: 24px;
    text-transform: uppercase;
    color: ${props => (props.in ? props.theme.positiveColor : props.theme.substitutionOutColor)};
    > span {
        display: inline-block;
        vertical-align: middle;
    }
    &:before {
        content: '';
        display: inline-block;
        margin-right: 10px;
        width: 0;
        height: 0;
        border-style: solid;
        border-width: 10px 0 10px 20px;
        border-color: transparent transparent transparent
            ${props => (props.in ? props.theme.positiveColor : props.theme.substitutionOutColor)};
        vertical-align: middle;
        ${props => (props.in ? 'transform: rotate(180deg);' : '')};
    }
`;

const EllipsisOverflow = styled.span`
    display: inline-block;
    text-overflow: ellipsis;
    max-width: 100%;
    overflow: hidden;
`;

const InfoWrapper = styled.div`
    text-align: center;
`;

const ShirtWrapper = styled.div`
    width: ${props => props.width || '320'}px;
    position:relative;
    margin: 0 auto;
    img {
        height: auto;
    }
`;

const MatchScoreWrapper = styled.div`
    .wdp-team-logo {
        height: 60px !important;
    }
    .team-name {
        width: calc(50% - 80px) !important;
    }
    .score-box {
        font-size: 57px !important;
        font-weight: 400 !important;
    }
`;

const getPlayerName = ({ shortFirstName, lastName }) => {
    return `${shortFirstName} ${lastName}`;
};

const getPlayerShirt = ({ event, player, incident, width }) => {
    let isHome = incident.home === 1;
    if (incident.typeId === 10) isHome = !isHome; // swap teams for own goal

    return (
        <ShirtWrapper width={width}>
            <PlayerLogo
                display="3d_jersey_back"
                number={_.get(player, 'shirtNumber')}
                playerId={_.get(player, 'id')}
                width={width || 320}
                seasonId={event.tournamentId}
                teamId={isHome ? event.homeId : event.awayId}
            />
        </ShirtWrapper>
    );
};

const Score = ({ incidentId, event, matchScore }) => {
    return (
        <MatchScoreWrapper className="__c__c5">
            <MatchScore
                {...matchScore}
                event={event}
                view="simpleLogoScore"
                styles={`font-size: 50px;`}
                incidentId={incidentId}
            />
        </MatchScoreWrapper>
    );
};

const Wrapper = ({ children }) => {
    return (
        <ContentWrapper>
            <MainContent>{children}</MainContent>
        </ContentWrapper>
    );
};

const IncidentInfo = (props, ctx) => {
    const lang = _.get(ctx, 'info.lang', 'en');
    const playerName = getPlayerName(props.playerName);
    const fontSize = playerName.length < 15 ? 47 : 30;

    return (
        <div>
            <EllipsisOverflow>
                <Text
                    style={{
                        maxWidth: '400px',
                        textOverflow: 'ellipsis',
                        overflow: 'hidden'
                    }}
                    className="__c__c1"
                    size={fontSize}
                >
                    {playerName}
                </Text>
            </EllipsisOverflow>
            <Space size={7} />
            <Score {...props} />
            <Text className="__c__c2" bold size={21} style={{ marginTop: '8px' }}>
                {props.incidentMinute} {i18('min', lang)}.
            </Text>
        </div>
    );
};

const Goal = (props, ctx) => {
    let lang = _.get(ctx, 'info.lang', 'en');

    return (
        <div>
            <Text size={92} bold className="__c__c3">
                {i18('title.goal', lang).toUpperCase()}
            </Text>
            <div>{getPlayerShirt({ ...props, width: 165 })}</div>
            <InfoWrapper>
                <IncidentInfo {...props} />
            </InfoWrapper>
        </div>
    );
};

const OwnGoal = (props, ctx) => {
    let lang = _.get(ctx, 'info.lang', 'en');

    return (
        <div>
            <Text size={55} bold style={{ color: props.incidentMinuteColor }} className="__c__c4">
                {i18('title.owngoal', lang).toUpperCase()}
            </Text>
            <div>{getPlayerShirt({ ...props, width: 165 })}</div>
            <InfoWrapper>
                <IncidentInfo {...props} />
            </InfoWrapper>
        </div>
    );
};

const Assist = (props, ctx) => {
    let lang = _.get(ctx, 'info.lang', 'en');
    return (
        <div>
            <Text size={92} bold className="__c__c3">
                {i18('title.assist', lang).toUpperCase()}
            </Text>
            <div>{getPlayerShirt({ ...props, width: 165 })}</div>
            <InfoWrapper>
                <IncidentInfo {...props} />
            </InfoWrapper>
        </div>
    );
};

const Substitution = (props, ctx) => {
    let lang = _.get(ctx, 'info.lang', 'en');

    return (
        <SubstitutionWrapper>
            <div className="flex">
                <div>
                    <div style={{ display: 'flex' }}>
                        <div>{getPlayerShirt({ ...props, player: props.player2, width: 120 })}</div>
                        <div className="t-shirt-subst-in">{getPlayerShirt({ ...props, width: 180 })}</div>
                    </div>
                </div>

                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center'
                    }}
                >
                    <SubstitutionType out>
                        <span>{i18('out', lang)}</span>
                    </SubstitutionType>
                    <Text size={25}>
                        <EllipsisOverflow>
                            <Text
                                className="__c__c14"
                                size={25}
                                style={{
                                    lineHeight: '1.3em',
                                    textOverflow: 'ellipsis',
                                    maxWidth: '200px'
                                }}
                            >
                                {getPlayerName(props.player2Name)}
                            </Text>
                        </EllipsisOverflow>
                    </Text>
                    <Space size={20} />
                    <SubstitutionType in>
                        <span>{i18('in', lang)}</span>
                    </SubstitutionType>
                    <Text size={25}>
                        <EllipsisOverflow>
                            <Text
                                className="__c__c14"
                                size={25}
                                style={{
                                    lineHeight: '1.3em',
                                    textOverflow: 'ellipsis',
                                    maxWidth: '200px'
                                }}
                            >
                                {getPlayerName(props.playerName)}
                            </Text>
                        </EllipsisOverflow>
                    </Text>
                </div>
            </div>

            <InfoWrapper>
                <Score {...props} />
                <Text className="__c__c13" size={21} bold style={{ textAlign: 'center', width: '100%' }}>
                    {props.incidentMinute} {i18('min', lang)}.
                </Text>
            </InfoWrapper>
        </SubstitutionWrapper>
    );
};

const Card = ({ cardColor, playerName, ...props }) => {
    return (
        <div>
            <CardIconWrapper>
                <SimpleCard width={36} height={50} color={cardColor} />
            </CardIconWrapper>
            <div>{getPlayerShirt({ ...props, width: 165 })}</div>
            <InfoWrapper>
                <IncidentInfo {...props} playerName={playerName} />
            </InfoWrapper>
        </div>
    );
};

Goal.contextTypes = {
    info: PropTypes.object
};

Wrapper.contextTypes = {
    info: PropTypes.object
};
Assist.contextTypes = {
    info: PropTypes.object
};
OwnGoal.contextTypes = {
    info: PropTypes.object
};
Card.contextTypes = {
    info: PropTypes.object
};
Substitution.contextTypes = {
    info: PropTypes.object
};

export default {
    Wrapper,
    Goal,
    Assist,
    OwnGoal,
    Card,
    Substitution,
    disableBranding: true
    //contentProps: { style: { height: 550 } }
};

export { Wrapper, Goal, Assist, OwnGoal, Card, Substitution };



/** WEBPACK FOOTER **
 ** ../src/widgets/components/sm/views/IncidentStyle4.js
 **/